/// <reference types="vite/client" />
import type { ROIModelResponse } from '../../services/roiService';
import { normalizeRoiModelResponse } from '../../services/roiService';
import { EA_CLIENT_ID } from '../constants/eaClient';

// src/prototype/services/useCaseExplorerService.ts
// ============================================================
// API client for /api/v2/use-case-explorer
// Covers: ranked opportunities list, single opportunity,
//         multi-tab evaluation, and selection management.
//
// Base route: /api/v2/use-case-explorer
// Version: 1.0  (matches use_case_explorer_api.md)
// ============================================================

const API_BASE = (
    import.meta.env.VITE_API_URL ||
    import.meta.env.VITE_BACKEND_URL ||
    'http://localhost:8080'
).replace(/\/+$/, '');

/** EA stage ROI routes are keyed to the same org as Personalization Strength. */
const ROI_MODEL_CLIENT_ID = EA_CLIENT_ID;

// ── Response types ───────────────────────────────────────────────────────────

export interface UCEOpportunity {
    use_case_id: number;
    rank: number | null;
    name: string;
    title?: string;
    description: string;
    category_name: string;
    category?: string;
    /** Strategic fit label from API (camelCase). */
    categoryFit?: string;
    /** Some payloads use snake_case; normalized into `categoryFit`. */
    category_fit?: string;
    priority_label?: string;
    value_score: number;
    implementation_score: number;
    total_score: number;
    financial_composite: number;
    effort_tier: 'Low' | 'Medium' | 'High';
    timeline: '0-3 months' | '3-6 months' | '6-9 months' | '9-12 months' | 'over 12 months' | string;
    /** Legacy: High / Emerging; newer API: Significant / Low. */
    value_band: 'High' | 'Moderate' | 'Emerging' | 'Significant' | 'Low' | string;
    is_selected: boolean;
    profile_path: string;
    /** use_cases.tenant_id — "standard" = library; org id = tenant-scoped. */
    tenant_id?: string | null;
    /** Set when this org row was cloned from the standard library. */
    standard_source_use_case_id?: number | null;
    /** standard | cloned | tenant_added */
    origin?: 'standard' | 'cloned' | 'tenant_added' | string;
    /** Preformatted range from API, e.g. `"$6.55M - $13.60M"` (cons–aggr from ROI model). */
    roi_range?: string | null;
    /** Annual benefit in dollars (base case); fallback when scenario columns are absent. */
    annual_benefit_base?: number;
    /** Conservative / aggressive annual benefits from use_case_roi_models. */
    annual_benefit_conservative?: number;
    annual_benefit_aggressive?: number;
    /** Base-case payback from use_case_roi_models (null when missing/0). */
    payback_months?: number | null;
    payback_months_conservative?: number | null;
    payback_months_aggressive?: number | null;
    ease_of_implementation_score?: number | null;
    primary_risk?: string;
}

export interface UCEPagination {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
}

export interface UCEAppliedFilters {
    search: string | null;
    category: string | null;
    effort: string | null;
    timeline: string | null;
    valueBand: string | null;
    valueMin: number | null;
    valueMax: number | null;
    onlySelected: boolean;
    sortBy: string;
    sortOrder: string;
}

export interface UCEPortfolioMetricsMeta {
    /** ISO timestamp when portfolio metrics were last computed (not request time). */
    last_computed_at: string | null;
    last_computed_source?: string | null;
}

export interface UCEListResponse {
    success: boolean;
    data: UCEOpportunity[];
    pagination: UCEPagination;
    selection: { selected_count: number; persistence: string };
    /** DMD-1063: last computed metadata for portfolio metrics. */
    metrics?: UCEPortfolioMetricsMeta;
    applied: UCEAppliedFilters;
}

// Evaluation tab payloads — fields vary by tab; typed as open records
export interface UCEEvaluationTabs {
    overview?: Record<string, unknown>;
    alignment?: Record<string, unknown>;
    roi_summary?: Record<string, unknown>;
    feasibility?: Record<string, unknown>;
    risks?: Record<string, unknown>;
    roadmap?: Record<string, unknown>;
    actions?: Record<string, unknown>;
}

export interface UCEEvaluationResponse {
    success: boolean;
    /** Legacy `{ use_case, tabs }` or flat `{ header, overview, … }` — `fetchEvaluation` normalizes the latter. */
    data: Record<string, unknown>;
}

function isRecord(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function num(v: unknown, fallback = 0): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/** Compact millions label aligned with list API (e.g. `$9.05M`). */
function formatUsdCompactM(dollars: number): string {
    const m = dollars / 1_000_000;
    return `$${m.toFixed(2)}M`;
}

/**
 * When the API omits `roi_range`, derive cons–aggr from ROI-model scenario benefits.
 * Falls back to base-only when conservative/aggressive are missing (no invented multipliers).
 */
export function deriveRoiRangeFromAnnualBenefit(
    annualBenefitBase: number,
    annualBenefitConservative?: number | null,
    annualBenefitAggressive?: number | null
): string | null {
    const base = Number.isFinite(annualBenefitBase) && annualBenefitBase > 0 ? annualBenefitBase : null;
    const cons =
        annualBenefitConservative != null &&
        Number.isFinite(annualBenefitConservative) &&
        annualBenefitConservative > 0
            ? annualBenefitConservative
            : base;
    const aggr =
        annualBenefitAggressive != null &&
        Number.isFinite(annualBenefitAggressive) &&
        annualBenefitAggressive > 0
            ? annualBenefitAggressive
            : base;
    if (cons == null || aggr == null) return null;
    const lo = Math.min(cons, aggr);
    const hi = Math.max(cons, aggr);
    if (lo === hi) return formatUsdCompactM(lo);
    return `${formatUsdCompactM(lo)} - ${formatUsdCompactM(hi)}`;
}

function annualBenefitDollars(row: UCEOpportunity): number {
    const a = row.annual_benefit_base;
    if (a != null && Number.isFinite(a) && a > 0) return a;
    const f = row.financial_composite;
    return Number.isFinite(f) && f > 0 ? f : 0;
}

import { sanitizeDisplayLabel } from '@/utils/sanitizeDisplayLabel';
import { dedupeGet, eaCaches, readTtl, writeTtl } from '../utils/sharedTtlCache';

/** Fills `roi_range` when the API returns null but a dollar-scale benefit exists. */
export function normalizeUCEOpportunity(row: UCEOpportunity): UCEOpportunity {
    const dollars = annualBenefitDollars(row);
    const missingRange = row.roi_range == null || row.roi_range === '';
    const derived =
        missingRange && dollars > 1_000
            ? deriveRoiRangeFromAnnualBenefit(
                  dollars,
                  row.annual_benefit_conservative,
                  row.annual_benefit_aggressive
              )
            : null;
    const categoryFit = row.categoryFit ?? row.category_fit;
    const name = sanitizeDisplayLabel(row.name);
    return {
        ...row,
        name: name || row.name,
        ...(categoryFit != null && categoryFit !== ''
          ? { categoryFit: sanitizeDisplayLabel(categoryFit) || categoryFit }
          : {}),
        ...(derived ? { roi_range: derived } : {}),
    };
}

/** True when API returns nested `header` + `overview` (not legacy `tabs`). */
export function isFlatUceEvaluationPayload(data: unknown): boolean {
    if (!isRecord(data)) return false;
    if (data.tabs && isRecord(data.tabs)) return false;
    return Boolean(data.header && data.overview);
}

function dollarsToAnnualBenefitM(raw: unknown): number {
    const n = num(raw);
    if (n <= 0) return 0;
    return n > 1_000 ? n / 1_000_000 : n;
}

/**
 * Number when the payload actually carries one, null when it does not (DMD-1159).
 * Keeps a reported 0 distinguishable from a missing value so the UI only shows `—`
 * when a figure is genuinely unknown.
 */
function numOrNull(v: unknown): number | null {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

/**
 * Timeline month for a milestone. Never treats `phase` (e.g. "Activation") as a month —
 * that coerced to 0 and left display order as arbitrary API order (DMD-1176).
 */
export function parseMonthsFromStart(m: Record<string, unknown>): number {
    const raw =
        m.months_from_start ??
        m.monthsFromStart ??
        m.month ??
        m.month_number ??
        m.monthNumber;
    const n = numOrNull(raw);
    return n != null ? n : 0;
}

/** Chronological order for Implementation Roadmap / Path to Value (DMD-1176). */
export function sortMilestonesByTimeline<
    T extends { months_from_start?: number | null; label?: unknown; name?: unknown },
>(milestones: T[]): T[] {
    return [...milestones].sort((a, b) => {
        const ma = Number(a.months_from_start);
        const mb = Number(b.months_from_start);
        const na = Number.isFinite(ma) ? ma : 0;
        const nb = Number.isFinite(mb) ? mb : 0;
        if (na !== nb) return na - nb;
        const la = String(a.label ?? a.name ?? '');
        const lb = String(b.label ?? b.name ?? '');
        return la.localeCompare(lb);
    });
}

/** Null-preserving dollars → millions, using the same magnitude heuristic as annual benefit. */
function dollarsToMOrNull(raw: unknown): number | null {
    const n = numOrNull(raw);
    if (n == null) return null;
    return Math.abs(n) > 1_000 ? n / 1_000_000 : n;
}

function formatMoneyMFromDollars(raw: unknown): string {
    const m = dollarsToAnnualBenefitM(raw);
    return m > 0 ? `$${m.toFixed(2)}M` : 'No value';
}

function avgAssumptionConfidence(assumptions: unknown): number {
    if (!Array.isArray(assumptions) || assumptions.length === 0) return 0;
    let sum = 0;
    let c = 0;
    for (const a of assumptions) {
        if (!isRecord(a)) continue;
        const conf = num(a.confidence, NaN);
        if (Number.isFinite(conf)) {
            sum += conf;
            c += 1;
        }
    }
    if (!c) return 0;
    return Math.round((sum / c) * 100);
}

function formatRoiAssumptionRow(a: Record<string, unknown>): { label: string; value: string } {
    const label = String(a.label ?? 'Assumption');
    const unit = String(a.unit ?? '').trim();
    const val = a.value;
    const src = a.source ? ` · ${String(a.source)}` : '';
    const st = a.source_type ? ` (${String(a.source_type)})` : '';
    let display = '';
    if (typeof val === 'number') {
        if (Math.abs(val) >= 1_000_000) display = `$${(val / 1_000_000).toFixed(2)}M`;
        else if (unit === '$') display = `$${val.toLocaleString()}`;
        else if (unit === '%' || /pct|percent/i.test(label)) {
            display =
                Math.abs(val) <= 1 && Math.abs(val) > 0 ? `${(val * 100).toFixed(1)}%` : `${val}${unit ? unit : ''}`;
        } else if (Math.abs(val) >= 10_000) display = val.toLocaleString();
        else display = String(val);
    } else {
        display = String(val ?? '');
    }
    return { label, value: `${display}${src}${st}`.trim() };
}

/** DMD-1171: hierarchical Immediate Next Steps (section + nested actions). */
export type ImmediateNextStep = {
    title: string;
    items: string[];
};

function cleanStepText(s: string): string {
    return s.replace(/\*+/g, '').replace(/\s+/g, ' ').trim();
}

function isImmediateNextStep(v: unknown): v is ImmediateNextStep {
    return (
        isRecord(v) &&
        typeof v.title === 'string' &&
        v.title.trim().length > 0 &&
        Array.isArray(v.items)
    );
}

/**
 * Parse overview.immediateNextSteps markdown into section headings + nested
 * bullets instead of flattening everything into one numbered list (DMD-1171).
 */
export function parseImmediateNextSteps(text: unknown): ImmediateNextStep[] {
    if (Array.isArray(text)) {
        return coerceImmediateNextSteps(text);
    }
    if (typeof text !== 'string' || !text.trim()) return [];

    const headingRe = /^(#{1,4})\s+(?:\d+[.)]\s*|Step\s+\d+:\s*)?(.+)$/i;
    const numberedRe = /^(\d+)[.)]\s+(.+)$/;
    const bulletRe = /^[-*]\s+(.+)$/;

    const groups: ImmediateNextStep[] = [];
    let current: ImmediateNextStep | null = null;

    for (const rawLine of text.split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!line) continue;

        const heading = line.match(headingRe);
        if (heading) {
            const title = cleanStepText(heading[2]);
            if (!title || title.length >= 180) continue;
            if (/following steps|implementation plan for/i.test(title)) continue;
            current = { title, items: [] };
            groups.push(current);
            continue;
        }

        const numbered = line.match(numberedRe);
        if (numbered) {
            const title = cleanStepText(numbered[2]);
            if (!title || title.length >= 180) continue;
            if (/following steps|implementation plan for/i.test(title)) continue;
            current = { title, items: [] };
            groups.push(current);
            continue;
        }

        const bullet = line.match(bulletRe);
        if (bullet) {
            const item = cleanStepText(bullet[1]);
            if (!item || item.length < 8 || item.length >= 280) continue;
            if (current) {
                current.items.push(item);
            } else {
                // Orphan bullet → treat as its own top-level step
                groups.push({ title: item, items: [] });
            }
            continue;
        }
    }

    // Fallback: no recognizable hierarchy — keep prior flat extraction behavior
    if (groups.length === 0) {
        const byPara = text
            .split(/\n\s*\n/)
            .map((p) => cleanStepText(p.replace(/^#{1,4}\s+/, '')))
            .filter((p) => p.length > 20 && p.length < 280);
        if (byPara.length >= 2) {
            return byPara.slice(0, 8).map((title) => ({ title, items: [] }));
        }
        const compact = cleanStepText(text);
        return compact ? [{ title: compact.slice(0, 280), items: [] }] : [];
    }

    return groups
        .filter((g) => g.title.length > 0)
        .slice(0, 10)
        .map((g) => ({
            title: g.title.replace(/^Step\s+\d+:\s*/i, ''),
            items: g.items.slice(0, 6),
        }));
}

/** Accept already-parsed groups, flat strings, or raw markdown. */
export function coerceImmediateNextSteps(raw: unknown): ImmediateNextStep[] {
    if (typeof raw === 'string') return parseImmediateNextSteps(raw);
    if (!Array.isArray(raw) || raw.length === 0) return [];

    if (raw.every(isImmediateNextStep)) {
        return (raw as ImmediateNextStep[])
            .map((g) => ({
                title: cleanStepText(g.title).replace(/^Step\s+\d+:\s*/i, ''),
                items: g.items
                    .map((i) => cleanStepText(String(i)))
                    .filter((i) => i.length >= 8)
                    .slice(0, 6),
            }))
            .filter((g) => g.title)
            .slice(0, 10);
    }

    // Flat string[] from older payloads — no hierarchy available
    if (raw.every((item) => typeof item === 'string')) {
        return (raw as string[])
            .map((s) => cleanStepText(s))
            .filter((s) => s.length >= 8)
            .slice(0, 12)
            .map((title) => ({ title, items: [] }));
    }

    // Mixed object shapes from actions tab
    const fromObjects: ImmediateNextStep[] = [];
    for (const item of raw) {
        if (!isRecord(item)) continue;
        const title = cleanStepText(
            String(
                item.title ??
                    item.label ??
                    item.step ??
                    item.action ??
                    item.text ??
                    item.narrative ??
                    ''
            )
        );
        if (!title) continue;
        const nested = item.items ?? item.sub_steps ?? item.subSteps ?? item.children;
        const items = Array.isArray(nested)
            ? nested
                  .map((n) =>
                      cleanStepText(
                          typeof n === 'string'
                              ? n
                              : String(
                                    isRecord(n)
                                        ? n.title ?? n.text ?? n.description ?? ''
                                        : ''
                                )
                      )
                  )
                  .filter((s) => s.length >= 8)
                  .slice(0, 6)
            : [];
        fromObjects.push({ title, items });
    }
    return fromObjects.slice(0, 10);
}

function capitalizeWord(s: string): string {
    if (!s) return s;
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** DMD-1173: curated Alignment Evidence card shown on the Decision Brief. */
export type AlignmentEvidencePoint = {
    label?: string;
    markdown: string;
    /** Only set when the API priority is meaningful (not a uniform Medium dump). */
    priority?: string;
    category?: string;
};

/**
 * Pipeline `alignmentEvidence` is often raw markdown fragments (phases, RACI,
 * procurement, infra) with identical Medium/0.5 tags. Keep only items that
 * support stakeholder alignment, structure them with labels/categories, and
 * drop meaningless priority badges.
 */
const ALIGNMENT_IRRELEVANT_RE =
    /^(phase\s*\d+|objective\s*:)|(\bphase\s*\d+\b.*\b(months?|initiation|planning|pilot|implementation)\b)|(\braci\b)|(\( ?[racai] ?\)\s*:)|(\bprocurement\b)|(\brisk(s)?\b\s*:)|(\bstakeholder resistance\b)|(\bshadow it\b)|(\byardsticks?\b)|(\bfinops\b)|(\biam\b)|(\bbackup\/?dr\b)|(\blanding zones?\b)|(\bthroughput\b)|(\buptime,?\s*incidents\b)|(\bcompute,?\s*storage\b)|(\bnetwork\/edge\b)|(\bvulnerability management\b)|(\bshowback\b)|(\bchargeback\b)|(\blicense (consolidation|rationalization)\b)/i;

const ALIGNMENT_RELEVANT_RE =
    /\b(stakeholder|alignment|sponsor|sponsors|engagement|buy-?in|town\s*halls?|huddles?|champion|champions|steering\s+committee|governance\s+council|executive\s+(update|sponsor|leadership|dialogue)|clinical\s+leaders?|decision\s+rights|charter|feedback|newsletters?|meeting\s+cadence|role-?based\s+enablement|service\s+line\s+champions?|co-?design|validate\s+workflows|intake\s+and\s+feedback|published\s+slas)\b/i;

const ALIGNMENT_SECTION_ONLY_RE =
    /^(speed and productivity|security and resilience|reliability\/performance|financial stewardship|compliance\/privacy\/security|security gaps|outcomes targeted|core:|platform:|culture:)\b/i;

function stripMarkdownDecor(s: string): string {
    return s.replace(/\*+/g, '').replace(/^#+\s*/, '').trim();
}

function extractEvidenceLabel(narrative: string): { label?: string; body: string } {
    const bold = narrative.match(/^\*\*([^*]{2,80})\*\*\s*:?\s*([\s\S]*)$/);
    if (bold) {
        const label = stripMarkdownDecor(bold[1]).replace(/:+$/, '').trim();
        const body = bold[2].trim();
        if (label && body) return { label, body };
        if (label) return { label, body: label };
    }
    const plain = narrative.match(/^([A-Z][^:]{2,70}):\s+(.+)$/);
    if (plain) {
        return { label: plain[1].trim(), body: plain[2].trim() };
    }
    return { body: narrative.trim() };
}

function categorizeAlignmentEvidence(text: string): string {
    const t = text.toLowerCase();
    if (/\b(sponsor|executive|ceo|coo|cfo|cio|cmio|cnio|leadership)\b/.test(t)) {
        return 'Sponsors & owners';
    }
    if (/\b(steering|governance|charter|decision rights|review board|council|arb)\b/.test(t)) {
        return 'Governance';
    }
    if (/\b(newsletter|town hall|huddle|meeting|communication|update|feedback)\b/.test(t)) {
        return 'Engagement & communication';
    }
    if (/\b(champion|enablement|training|co-design|workflow|adoption)\b/.test(t)) {
        return 'Adoption & enablement';
    }
    return 'Stakeholder alignment';
}

function formatEvidencePriority(raw: string | undefined, score: number | null): string | undefined {
    const p = (raw || '').trim().toLowerCase();
    if (p === 'high' || p === 'low') return capitalizeWord(p);
    if (score != null && Number.isFinite(score)) {
        if (score >= 0.75) return 'High';
        if (score <= 0.35) return 'Low';
    }
    return undefined;
}

export function normalizeAlignmentEvidence(raw: unknown): AlignmentEvidencePoint[] {
    if (!Array.isArray(raw)) return [];

    type Scored = AlignmentEvidencePoint & { _score: number; _priorityRaw: string };
    const scored: Scored[] = [];

    for (const item of raw) {
        let narrative = '';
        let priorityRaw = '';
        let score: number | null = null;
        let providedLabel = '';

        if (typeof item === 'string') {
            narrative = item.trim();
        } else if (isRecord(item)) {
            narrative = String(
                item.narrative ?? item.markdown ?? item.evidence ?? item.summary ?? item.text ?? ''
            ).trim();
            priorityRaw = String(item.priority ?? item.priorityBand ?? '').trim();
            const n = Number(item.score);
            score = Number.isFinite(n) ? n : null;
            providedLabel = String(item.label ?? item.title ?? item.category ?? '').trim();
        } else {
            continue;
        }

        if (!narrative || narrative.length < 12) continue;

        const plain = stripMarkdownDecor(narrative);
        if (ALIGNMENT_SECTION_ONLY_RE.test(plain) && plain.length < 60) continue;
        if (ALIGNMENT_IRRELEVANT_RE.test(plain)) continue;

        const relevant = ALIGNMENT_RELEVANT_RE.test(plain);
        // Keep unlabeled outcome-style lines only when they clearly reference people/org alignment.
        if (!relevant) continue;

        const extracted = extractEvidenceLabel(narrative);
        const label = providedLabel || extracted.label;
        const body = extracted.body || plain;
        if (!body || body.length < 12) continue;

        const category = categorizeAlignmentEvidence(`${label ?? ''} ${body}`);
        const priority = formatEvidencePriority(priorityRaw, score);
        let rank = 1;
        if (/\bstakeholder|alignment|sponsor\b/i.test(body)) rank += 2;
        if (label) rank += 1;
        if (priority === 'High') rank += 1;

        scored.push({
            ...(label ? { label } : {}),
            markdown: body,
            ...(priority ? { priority } : {}),
            category,
            _score: rank,
            _priorityRaw: priorityRaw.toLowerCase(),
        });
    }

    // Dedupe near-identical bodies
    const seen = new Set<string>();
    const unique = scored.filter((e) => {
        const key = e.markdown.toLowerCase().replace(/\s+/g, ' ').slice(0, 120);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    unique.sort((a, b) => b._score - a._score);

    const priorities = new Set(unique.map((e) => e._priorityRaw).filter(Boolean));
    const uniformMedium =
        priorities.size <= 1 &&
        (priorities.size === 0 || (priorities.has('medium') && priorities.size === 1));

    return unique.slice(0, 8).map(({ _score, _priorityRaw, priority, ...rest }) => ({
        ...rest,
        ...(!uniformMedium && priority ? { priority } : {}),
    }));
}

/**
 * Maps flat evaluation API (`header`, `overview`, `alignment`, `roi`, …) into the legacy
 * `{ use_case, tabs }` shape expected by EAUseCaseBriefPage.
 */
export function normalizeEvaluationPayload(data: unknown): Record<string, unknown> {
    if (!isRecord(data)) return data as Record<string, unknown>;
    if (data.tabs && isRecord(data.tabs)) return data;

    if (!data.header || !data.overview) return data;

    const header = isRecord(data.header) ? data.header : {};
    const overview = isRecord(data.overview) ? data.overview : {};
    const alignment = isRecord(data.alignment) ? data.alignment : {};
    const roi = isRecord(data.roi) ? data.roi : {};
    const feasibility = isRecord(data.feasibility) ? data.feasibility : {};
    const roadmap = isRecord(data.roadmap) ? data.roadmap : {};
    const actions = isRecord(data.actions) ? data.actions : {};
    const risksRaw = Array.isArray(data.risks) ? data.risks : [];

    const valueScore = num(header.valueScore);
    const implementationScore = num(header.implementationScore);
    const totalScore = (valueScore + implementationScore) / 2;
    const rank = num(header.portfolioRank);
    const lightBadge = String(header.lightBadge ?? '');
    const title = String(
        header.title ?? header.name ?? overview.title ?? overview.name ?? ''
    ).trim();
    const category = String(
        header.categoryName ??
            header.category_name ??
            header.category ??
            overview.categoryName ??
            overview.category_name ??
            overview.category ??
            ''
    ).trim();
    const useCaseId = header.useCaseId ?? header.use_case_id ?? data.useCaseId ?? data.use_case_id;

    const roiAssumptions = Array.isArray(roi.roiAssumptions)
        ? (roi.roiAssumptions as unknown[]).filter(isRecord).map((r) => formatRoiAssumptionRow(r))
        : [];

    const annualRaw = roi.annualRoiRange ?? header.annualRoi;

    /** Explicit API decision (DMD-1160) — null lets the UI flag a score-derived verdict. */
    const recommendationRaw =
        String(header.recommendation ?? header.verdict ?? header.decision ?? data.recommendation ?? '').trim() ||
        null;

    /** Authoritative percentile (DMD-1161) — null means the UI omits the percentile label. */
    const percentile = numOrNull(header.percentile ?? header.portfolioPercentile ?? data.percentile);

    /** Per-use-case score freshness (DMD-1158) rather than portfolio-wide catalog metrics. */
    const lastComputedAt =
        header.lastComputedAt ?? header.last_computed_at ?? data.lastComputedAt ?? data.last_computed_at ?? null;
    const lastComputedSource =
        header.lastComputedSource ??
        header.last_computed_source ??
        data.lastComputedSource ??
        data.last_computed_source ??
        null;

    const timeToValueMonths = numOrNull(roi.timeToValue ?? header.timeToValue);

    const risksForTab = risksRaw.map((r) => {
        if (!isRecord(r)) return r;
        const sev = capitalizeWord(String(r.severity ?? 'Medium'));
        const like = capitalizeWord(String(r.likelihood ?? 'Medium'));
        const desc = String(r.description ?? r.rationale ?? r.mitigation ?? '');
        return {
            ...r,
            name: r.name,
            severity: sev,
            likelihood: like,
            description: desc,
            mitigation: String(r.mitigation ?? ''),
            owner: String(r.owner ?? 'Leadership Team'),
        };
    });

    const milestones = Array.isArray(roadmap.milestones)
        ? sortMilestonesByTimeline(
              (roadmap.milestones as unknown[])
                  .map((m) => {
                      if (!isRecord(m)) return null;
                      const note = String(
                          m.value_note ??
                              m.description ??
                              m.relative_timing ??
                              m.relativeTiming ??
                              ''
                      ).trim();
                      const expectedOutcome = String(
                          m.expected_outcome ?? m.expectedOutcome ?? m.outcome ?? ''
                      ).trim();
                      return {
                          ...m,
                          label: m.label ?? m.name,
                          months_from_start: parseMonthsFromStart(m),
                          value_note: note,
                          expected_outcome: expectedOutcome,
                          // Tracking status from uc_milestone_tracking (Todo / In Progress / Done via UI).
                          milestone_id: m.milestone_id ?? m.milestoneId ?? null,
                          status: m.status ?? m.phase_status ?? m.tracking_status ?? null,
                          percent_complete: numOrNull(
                              m.percent_complete ??
                                  m.percentComplete ??
                                  m.tracking_completion_pct ??
                                  m.progress_pct ??
                                  m.progressPercent
                          ),
                          owner: String(m.owner ?? m.owner_role ?? 'Programme Team'),
                      };
                  })
                  .filter((m): m is Record<string, unknown> => m != null)
          )
        : [];

    // DMD-1173: filter unstructured pipeline fragments down to stakeholder-alignment evidence.
    const evidencePoints = normalizeAlignmentEvidence(
        alignment.alignmentEvidence ?? alignment.evidence_points ?? alignment.evidencePoints
    );

    return {
        use_case: {
            ...(useCaseId != null && useCaseId !== '' ? { use_case_id: useCaseId } : {}),
            ...(title ? { title, name: title } : {}),
            ...(category ? { category, category_name: category } : {}),
            value_score: valueScore,
            implementation_score: implementationScore,
            total_score: totalScore,
            rank,
            percentile,
            recommendation: recommendationRaw,
            last_computed_at: lastComputedAt,
            last_computed_source: lastComputedSource,
            priority_label: lightBadge,
            light_badge: lightBadge,
            effort_tier: implementationScore >= 60 ? 'Low' : implementationScore >= 40 ? 'Medium' : 'High',
        },
        tabs: {
            overview: {
                summary: overview.summary,
                description: overview.description,
                strategic_context: overview.strategicContext,
                organizational_impact: overview.orgImpact,
                operational_impact: overview.operationImpact,
                clinical_impact: overview.clinicalImpact,
                executive_decision: overview.executiveDecision,
                rationale: overview.executiveDecision,
                immediate_next_steps: parseImmediateNextSteps(overview.immediateNextSteps),
                next_steps: parseImmediateNextSteps(overview.immediateNextSteps),
                // Keep raw markdown so Brief can re-parse after HMR / older cache shapes (DMD-1171).
                immediate_next_steps_markdown:
                    typeof overview.immediateNextSteps === 'string'
                        ? overview.immediateNextSteps
                        : null,
                key_metrics: {
                    value_score: valueScore,
                    implementation_score: implementationScore,
                    total_score: totalScore,
                    rank,
                    percentile,
                },
            },
            alignment: {
                strategic_narrative: alignment.strategicNarrative,
                narrative: alignment.strategicNarrative,
                alignment_summary: alignment.alignmentSummary,
                stakeholder_alignment: alignment.alignmentSummary,
                operational_impact: alignment.operationImpact ?? alignment.operationalImpact,
                clinical_impact: alignment.clinicalImpact,
                organizational_impact: alignment.orgImpact ?? alignment.organizationalImpact,
                evidence_points: evidencePoints,
            },
            roi_summary: {
                annual_benefit_m: dollarsToMOrNull(annualRaw),
                annual_benefit: dollarsToMOrNull(annualRaw),
                implementation_cost_m: dollarsToMOrNull(roi.implementationCost),
                // Payback no longer borrows time-to-value — they are different measures.
                payback_months: numOrNull(roi.paybackMonths),
                npv_3yr_m: dollarsToMOrNull(roi.npv3Year),
                time_to_roi: timeToValueMonths != null ? `${timeToValueMonths} months` : null,
                roi_range: formatMoneyMFromDollars(annualRaw),
                roi_narrative: roi.roiNarrative,
                narrative: roi.roiNarrative,
                assumptions: roiAssumptions,
                confidence: avgAssumptionConfidence(roi.roiAssumptions) || Math.round(totalScore),
            },
            feasibility: {
                complexity_summary: feasibility.complexitySummary,
                technology_readiness: feasibility.technologyReadiness,
                data_availability: feasibility.dataAvailability,
                organizational_readiness: feasibility.organizationalReadiness,
                integration_complexity: feasibility.integrationComplexity,
                operational_dependencies: feasibility.operationalDependencies,
                change_management: feasibility.changeManagement,
                requirements: feasibility.requirements ?? feasibility.implementationRequirements,
            },
            risks: { risks: risksForTab },
            roadmap: {
                milestones,
                // Canonical top-level list first (DMD-1156), then the legacy roadmap-scoped keys.
                requirements:
                    data.implementation_requirements ??
                    data.implementationRequirements ??
                    roadmap.requirements ??
                    roadmap.implementationRequirements ??
                    roadmap.implementation_requirements ??
                    roadmap.dependencies ??
                    [],
            },
            actions: {
                immediate: actions.immediateActions ?? [],
                near_term: actions.nearTermActions ?? [],
                strategic: actions.strategicActions ?? [],
            },
        },
    };
}

/** GET /selection — current portfolio selection for a client. */
export interface UCESelectionState {
    selected_ids: number[];
    selected_count: number;
}

/** POST/DELETE /selection/:useCaseId — add or remove one use case. */
export interface UCESelectionActionResponse {
    success: boolean;
    client_id: string;
    use_case_id: number;
    decision_status: 'selected' | 'deselected';
    message: string;
}

/** @deprecated Use UCESelectionState */
export type UCESelectionResponse = UCESelectionState;

export interface UCEOpportunityFilters {
    q?: string;
    page?: number | string;
    pageSize?: number | string;
    sortBy?: 'rank' | 'name' | 'value' | 'implementation' | 'total_score' | 'financial' | 'timeline' | 'payback';
    sortOrder?: 'asc' | 'desc';
    category?: string;
    effort?: string;
    timeline?: string;
    valueBand?: string;
    /** Annual benefit dollars (financial_composite); max is exclusive */
    valueMin?: number;
    valueMax?: number;
    onlySelected?: boolean;
    selectedUseCaseIds?: string;
    /**
     * DMD-1064: filter use_cases.tenant_id.
     * Omit / "all" = full list; "standard" = library; org id (e.g. "300") = that tenant.
     */
    tenantId?: string;
}

export type UCEScenarioId = 'conservative' | 'base' | 'aggressive' | 'custom';

/** @deprecated Prefer ROIModelResponse from src/services/roiService */
export type UCERoiModelResponse = ROIModelResponse;

// ── Fetch helpers ─────────────────────────────────────────────────────────────
//
// Shared TTL + in-flight maps live on globalThis so Vite HMR / StrictMode remounts
// do not drop an in-flight GET and fire it twice.

const LIST_CACHE_TTL_MS = 60_000;
const DETAIL_CACHE_TTL_MS = 60_000;

function opportunitiesUrl(clientId: string, filters: UCEOpportunityFilters = {}): string {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
        if (v != null && v !== '') params.set(k, String(v));
    });
    return `${API_BASE}/api/v2/use-case-explorer/${clientId}/opportunities?${params}`;
}

function evaluationUrl(clientId: string, useCaseId: number | string): string {
    return `${API_BASE}/api/v2/use-case-explorer/${clientId}/opportunities/${useCaseId}/evaluation`;
}

function roiModelUrl(useCaseId: number | string, scenario: UCEScenarioId = 'base'): string {
    return `${API_BASE}/api/v2/use-case-explorer/${ROI_MODEL_CLIENT_ID}/opportunities/${encodeURIComponent(String(useCaseId))}/roi-model?scenario=${scenario}`;
}

function selectionUrl(clientId: string): string {
    return `${API_BASE}/api/v2/use-case-explorer/${clientId}/selection`;
}

function evaluateStateUrl(clientId: string): string {
    return `${API_BASE}/api/v2/use-case-explorer/${clientId}/evaluate-state`;
}

/** Durable EA Evaluate state (shortlist / finalists / order / flagged assumptions). */
export type InitiativeShortlistPayload = {
    launch: string[];
    pilot: string[];
    monitor: string[];
    defer: string[];
};

export interface EvaluateStatePayload {
    finalist_ids: string[];
    finalist_order: string[];
    initiative_shortlist: InitiativeShortlistPayload;
    /** Map entries: [ `${oppId}-${assumptionId}`, FlagType ] */
    flagged_assumptions: Array<[string, string]>;
}

export interface EvaluateStateResponse {
    success: boolean;
    client_id: string;
    state: EvaluateStatePayload;
    updated_at: string | null;
    updated_by: string | null;
    persistence: string;
    exists: boolean;
    posture_sync?: { updated?: number; cleared?: boolean; error?: string } | null;
}

/** True when the label is a numeric placeholder like "Use Case 11" (DMD-1178 / DMD-1180). */
export function isPlaceholderUseCaseTitle(title: unknown): boolean {
    return /^Use Case\s+\d+$/i.test(String(title ?? '').trim());
}

export type CatalogBriefSnapshot = {
    id: string;
    title: string;
    category: string;
    totalScore?: number;
    valueBand?: string;
};

function catalogMetaKey(clientId: string, useCaseId: string | number): string {
    return `${clientId}:${useCaseId}`;
}

function snapshotFromOpportunity(
    opportunity: UCEOpportunity,
): CatalogBriefSnapshot | null {
    const id = String(opportunity.use_case_id ?? '');
    const title = String(opportunity.name ?? opportunity.title ?? '').trim();
    if (!id || !title || isPlaceholderUseCaseTitle(title)) return null;
    return {
        id,
        title,
        category: String(opportunity.category_name ?? opportunity.category ?? '').trim(),
        totalScore: typeof opportunity.total_score === 'number' ? opportunity.total_score : undefined,
        valueBand: opportunity.value_band != null ? String(opportunity.value_band) : undefined,
    };
}

/** Session-durable header meta — does not expire with the 60s list/detail TTL (DMD-1180). */
function rememberCatalogMeta(clientId: string, opportunity: UCEOpportunity): void {
    const snap = snapshotFromOpportunity(opportunity);
    if (!snap) return;
    eaCaches().catalogMeta.set(catalogMetaKey(clientId, snap.id), snap);
}

function rememberOpportunity(clientId: string, opportunity: UCEOpportunity): void {
    writeTtl(eaCaches().opportunity, `${clientId}:${opportunity.use_case_id}`, opportunity);
    rememberCatalogMeta(clientId, opportunity);
}

export function peekOpportunities(
    clientId: string,
    filters: UCEOpportunityFilters = {},
): UCEListResponse | null {
    return readTtl(eaCaches().list, opportunitiesUrl(clientId, filters), LIST_CACHE_TTL_MS);
}

/**
 * Header snapshot for Brief/ROI/Rationale.
 * Prefers session-durable catalog meta so prev/next still has the real name after the
 * 60s opportunities TTL expires (DMD-1180: stay ~1 min then click next → "Use Case N").
 */
export function peekCatalogBriefSnapshot(
    clientId: string,
    useCaseId: string | number,
): CatalogBriefSnapshot | null {
    const id = String(useCaseId);
    if (!id) return null;

    const durable = eaCaches().catalogMeta.get(catalogMetaKey(String(clientId), id));
    if (durable && typeof durable === 'object') {
        const snap = durable as CatalogBriefSnapshot;
        if (snap.title && !isPlaceholderUseCaseTitle(snap.title)) return snap;
    }

    const list = peekOpportunities(String(clientId), UCE_CATALOG_FILTERS);
    const row = list?.data?.find((o) => String(o.use_case_id) === id);
    if (row) {
        const fromList = snapshotFromOpportunity(row);
        if (fromList) {
            rememberCatalogMeta(String(clientId), row);
            return fromList;
        }
    }

    const cachedOpp = readTtl<UCEOpportunity>(
        eaCaches().opportunity,
        `${clientId}:${id}`,
        DETAIL_CACHE_TTL_MS,
    );
    if (cachedOpp) {
        const fromOpp = snapshotFromOpportunity(cachedOpp);
        if (fromOpp) {
            rememberCatalogMeta(String(clientId), cachedOpp);
            return fromOpp;
        }
    }

    return null;
}

/**
 * Resolve a header snapshot for prev/next navigation, refreshing catalog/opportunity
 * when the 60s TTL cache is cold (DMD-1180).
 */
export async function ensureCatalogBriefSnapshot(
    clientId: string,
    useCaseId: string | number,
): Promise<CatalogBriefSnapshot | null> {
    const id = String(useCaseId);
    if (!id) return null;

    const existing = peekCatalogBriefSnapshot(clientId, id);
    if (existing) return existing;

    try {
        await fetchOpportunities(String(clientId), UCE_CATALOG_FILTERS);
        const fromList = peekCatalogBriefSnapshot(clientId, id);
        if (fromList) return fromList;
    } catch {
        // Fall through to single-opportunity fetch.
    }

    try {
        const opportunity = await fetchOpportunity(String(clientId), id);
        return peekCatalogBriefSnapshot(clientId, id) ?? snapshotFromOpportunity(opportunity);
    } catch {
        return null;
    }
}

export function peekEvaluation(
    clientId: string,
    useCaseId: number | string,
): UCEEvaluationResponse | null {
    return readTtl(eaCaches().evaluation, evaluationUrl(clientId, useCaseId), DETAIL_CACHE_TTL_MS);
}

/** Drop cached evaluation so the next fetch picks up milestone status updates (DMD-1170). */
export function invalidateEvaluation(clientId: string, useCaseId: number | string): void {
    eaCaches().evaluation.delete(evaluationUrl(clientId, useCaseId));
}

export function peekRoiModel(
    useCaseId: number | string,
    scenario: UCEScenarioId = 'base',
): ROIModelResponse | null {
    return readTtl(eaCaches().roi, roiModelUrl(useCaseId, scenario), DETAIL_CACHE_TTL_MS);
}

function invalidateSelection(clientId: string): void {
    eaCaches().selection.delete(selectionUrl(clientId));
    eaCaches().inflight.delete(selectionUrl(clientId));
}

/** Shared catalog query used by Evaluate, Brief, ROI, and Rationale. */
export const UCE_CATALOG_FILTERS: UCEOpportunityFilters = {
    page: 1,
    pageSize: 200,
    sortBy: 'rank',
    sortOrder: 'asc',
};

/**
 * GET /api/v2/use-case-explorer/:clientId/opportunities
 * Returns a paginated, filtered, sorted list of opportunities.
 */
export async function fetchOpportunities(
    clientId: string,
    filters: UCEOpportunityFilters = {}
): Promise<UCEListResponse> {
    const url = opportunitiesUrl(clientId, filters);
    const cached = peekOpportunities(clientId, filters);
    if (cached) return cached;

    return dedupeGet(url, async () => {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`UCE API ${res.status} — ${url}`);
        const body = (await res.json()) as UCEListResponse;
        const value: UCEListResponse = {
            ...body,
            data: Array.isArray(body.data) ? body.data.map(normalizeUCEOpportunity) : [],
        };
        writeTtl(eaCaches().list, url, value);
        value.data.forEach((opportunity) => rememberOpportunity(clientId, opportunity));
        return value;
    });
}

/**
 * GET /api/v2/use-case-explorer/:clientId/opportunities/:useCaseId
 * Returns a single opportunity object.
 */
export async function fetchOpportunity(
    clientId: string,
    useCaseId: number | string
): Promise<UCEOpportunity> {
    const cacheKey = `${clientId}:${useCaseId}`;
    const cached = readTtl<UCEOpportunity>(eaCaches().opportunity, cacheKey, DETAIL_CACHE_TTL_MS);
    if (cached) return cached;

    const url = `${API_BASE}/api/v2/use-case-explorer/${clientId}/opportunities/${useCaseId}`;
    return dedupeGet(url, async () => {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`UCE API ${res.status} — ${url}`);
        const body = await res.json();
        if (body?.data == null) throw new Error(`UCE API missing data — ${url}`);
        const opportunity = normalizeUCEOpportunity(body.data as UCEOpportunity);
        rememberOpportunity(clientId, opportunity);
        return opportunity;
    });
}

/**
 * GET /api/v2/use-case-explorer/:clientId/opportunities/:useCaseId/evaluation
 * Returns the full multi-tab evaluation payload.
 */
export async function fetchEvaluation(
    clientId: string,
    useCaseId: number | string
): Promise<UCEEvaluationResponse> {
    const url = evaluationUrl(clientId, useCaseId);
    const cached = peekEvaluation(clientId, useCaseId);
    if (cached) return cached;

    return dedupeGet(url, async () => {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`UCE Evaluation API ${res.status} — ${url}`);
        const body = (await res.json()) as UCEEvaluationResponse;
        if (body?.data && typeof body.data === 'object') {
            body.data = normalizeEvaluationPayload(body.data);
        }
        writeTtl(eaCaches().evaluation, url, body);
        return body;
    });
}

/**
 * GET /api/v2/use-case-explorer/:clientId/opportunities/:useCaseId/evaluation/:tab
 * Returns a single evaluation tab payload.
 */
export async function fetchEvaluationTab(
    clientId: string,
    useCaseId: number | string,
    tab: 'overview' | 'alignment' | 'roi_summary' | 'feasibility' | 'risks' | 'roadmap' | 'actions'
): Promise<{ success: boolean; data: { use_case: UCEOpportunity; tab: string; content: Record<string, unknown>; sources: Record<string, unknown> } }> {
    const url = `${API_BASE}/api/v2/use-case-explorer/${clientId}/opportunities/${useCaseId}/evaluation/${tab}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`UCE Tab API ${res.status} — ${url}`);
    return res.json();
}

function normalizeSelectionState(body: Record<string, unknown>): UCESelectionState {
    const rawIds = body.selected_ids ?? body.selected_use_case_ids;
    const selected_ids = Array.isArray(rawIds)
        ? rawIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
        : [];
    const selected_count =
        typeof body.selected_count === 'number' ? body.selected_count : selected_ids.length;
    return { selected_ids, selected_count };
}

/**
 * GET /api/v2/use-case-explorer/:clientId/selection
 * Returns current portfolio selection.
 */
export async function getSelection(clientId: string): Promise<UCESelectionState> {
    const url = selectionUrl(clientId);
    const cached = readTtl<UCESelectionState>(eaCaches().selection, url, DETAIL_CACHE_TTL_MS);
    if (cached) return cached;

    return dedupeGet(url, async () => {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`UCE Selection API ${res.status} — ${url}`);
        const body = (await res.json()) as Record<string, unknown>;
        const value = normalizeSelectionState(body);
        writeTtl(eaCaches().selection, url, value);
        return value;
    });
}

/**
 * POST /api/v2/use-case-explorer/:clientId/selection/:useCaseId
 * Adds a use case to the portfolio selection.
 */
export async function selectUseCase(
    clientId: string,
    useCaseId: number
): Promise<UCESelectionActionResponse> {
    invalidateSelection(clientId);
    const url = `${API_BASE}/api/v2/use-case-explorer/${clientId}/selection/${useCaseId}`;
    const res = await fetch(url, { method: 'POST', credentials: 'include' });
    if (!res.ok) throw new Error(`UCE Selection POST API ${res.status} — ${url}`);
    return res.json();
}

/**
 * DELETE /api/v2/use-case-explorer/:clientId/selection/:useCaseId
 * Removes a use case from the portfolio selection.
 */
export async function deselectUseCase(
    clientId: string,
    useCaseId: number
): Promise<UCESelectionActionResponse> {
    invalidateSelection(clientId);
    const url = `${API_BASE}/api/v2/use-case-explorer/${clientId}/selection/${useCaseId}`;
    const res = await fetch(url, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw new Error(`UCE Selection DELETE API ${res.status} — ${url}`);
    return res.json();
}

/** @deprecated Use deselectUseCase */
export const removeSelection = deselectUseCase;

function emptyEvaluateState(): EvaluateStatePayload {
    return {
        finalist_ids: [],
        finalist_order: [],
        initiative_shortlist: { launch: [], pilot: [], monitor: [], defer: [] },
        flagged_assumptions: [],
    };
}

function normalizeEvaluateStatePayload(raw: unknown): EvaluateStatePayload {
    const empty = emptyEvaluateState();
    if (!raw || typeof raw !== 'object') return empty;
    const body = raw as Record<string, unknown>;

    const toIds = (value: unknown): string[] =>
        Array.isArray(value)
            ? value.map((id) => String(id)).filter((id) => id.length > 0)
            : [];

    const shortlistRaw =
        (body.initiative_shortlist as Record<string, unknown> | undefined) ||
        (body.initiativeShortlist as Record<string, unknown> | undefined) ||
        {};

    const flaggedRaw =
        body.flagged_assumptions ?? body.flaggedAssumptions ?? [];

    let flagged_assumptions: Array<[string, string]> = [];
    if (Array.isArray(flaggedRaw)) {
        flagged_assumptions = flaggedRaw
            .filter((entry): entry is [unknown, unknown] => Array.isArray(entry) && entry.length >= 2)
            .map(([key, flag]) => [String(key), String(flag)] as [string, string])
            .filter(([key, flag]) => key.length > 0 && flag.length > 0);
    } else if (flaggedRaw && typeof flaggedRaw === 'object') {
        flagged_assumptions = Object.entries(flaggedRaw as Record<string, unknown>).map(
            ([key, flag]) => [String(key), String(flag)] as [string, string],
        );
    }

    return {
        finalist_ids: toIds(body.finalist_ids ?? body.finalistIds),
        finalist_order: toIds(body.finalist_order ?? body.finalistOrder),
        initiative_shortlist: {
            launch: toIds(shortlistRaw.launch),
            pilot: toIds(shortlistRaw.pilot),
            monitor: toIds(shortlistRaw.monitor),
            defer: toIds(shortlistRaw.defer),
        },
        flagged_assumptions,
    };
}

/**
 * GET /api/v2/use-case-explorer/:clientId/evaluate-state
 * Durable Initiative Shortlist + Evaluate state (replaces localStorage source of truth).
 */
export async function getEvaluateState(clientId: string): Promise<EvaluateStateResponse> {
    const url = evaluateStateUrl(clientId);
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error(`UCE Evaluate-State API ${res.status} — ${url}`);
    const body = (await res.json()) as Record<string, unknown>;
    const state = normalizeEvaluateStatePayload(body.state ?? body);
    return {
        success: body.success !== false,
        client_id: String(body.client_id ?? clientId),
        state,
        updated_at: body.updated_at != null ? String(body.updated_at) : null,
        updated_by: body.updated_by != null ? String(body.updated_by) : null,
        persistence: String(body.persistence ?? 'bigquery'),
        exists: Boolean(body.exists),
        posture_sync: (body.posture_sync as EvaluateStateResponse['posture_sync']) ?? null,
    };
}

/**
 * PUT /api/v2/use-case-explorer/:clientId/evaluate-state
 * Upserts evaluate state (partial merge supported server-side).
 */
export async function saveEvaluateState(
    clientId: string,
    state: Partial<EvaluateStatePayload>,
): Promise<EvaluateStateResponse> {
    const url = evaluateStateUrl(clientId);
    const res = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(state),
    });
    if (!res.ok) {
        let message = `UCE Evaluate-State PUT API ${res.status}`;
        try {
            const body = await res.json();
            if (body?.error || body?.message) message = body.error || body.message;
        } catch {
            // ignore non-json error body
        }
        throw new Error(message);
    }
    const body = (await res.json()) as Record<string, unknown>;
    return {
        success: body.success !== false,
        client_id: String(body.client_id ?? clientId),
        state: normalizeEvaluateStatePayload(body.state ?? state),
        updated_at: body.updated_at != null ? String(body.updated_at) : null,
        updated_by: body.updated_by != null ? String(body.updated_by) : null,
        persistence: String(body.persistence ?? 'bigquery'),
        exists: true,
        posture_sync: (body.posture_sync as EvaluateStateResponse['posture_sync']) ?? null,
    };
}

/**
 * GET /api/v2/use-case-explorer/:clientId/opportunities/:useCaseId/roi-model
 * Returns ROI model data for the requested scenario.
 */
export async function fetchRoiModel(
    clientId: string,
    useCaseId: number | string,
    scenario: UCEScenarioId = 'base'
): Promise<ROIModelResponse> {
    const url = roiModelUrl(useCaseId, scenario);
    const cached = peekRoiModel(useCaseId, scenario);
    if (cached) return cached;

    return dedupeGet(url, async () => {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) {
            let message = `UCE ROI API ${res.status} — ${url}`;
            try {
                const body = await res.json();
                if (body?.message) message = body.message;
            } catch {
                // ignore non-json error body
            }
            throw new Error(message);
        }
        const body = await res.json();
        const normalized = normalizeRoiModelResponse(body, { financialScenario: scenario });
        writeTtl(eaCaches().roi, url, normalized);
        return normalized;
    });
}

/**
 * PUT /api/v2/use-case-explorer/:clientId/opportunities/:useCaseId/roi-model/custom-assumptions
 * Persists Assumptions Laboratory Custom overrides on use_case_roi_models.custom_assumptions.
 */
export async function saveCustomAssumptions(
    clientId: string,
    useCaseId: number | string,
    customAssumptions: Record<string, number>
): Promise<{ success: boolean; data?: { customAssumptions: Record<string, number> } }> {
    const effectiveClientId = ROI_MODEL_CLIENT_ID;
    const url = `${API_BASE}/api/v2/use-case-explorer/${effectiveClientId}/opportunities/${encodeURIComponent(String(useCaseId))}/roi-model/custom-assumptions`;
    const res = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customAssumptions }),
    });
    if (!res.ok) {
        let message = `UCE ROI custom assumptions API ${res.status}`;
        try {
            const body = await res.json();
            if (body?.message || body?.error) message = body.message || body.error;
        } catch {
            // ignore non-json error body
        }
        throw new Error(message);
    }
    return res.json();
}

/**
 * GET /api/v2/use-case-explorer/:clientId/opportunities/:useCaseId/roi-model/export
 * Downloads ROI CSV.
 */
export async function exportRoiModelCsv(
    clientId: string,
    useCaseId: number | string
): Promise<void> {
    const effectiveClientId = ROI_MODEL_CLIENT_ID;
    const url = `${API_BASE}/api/v2/use-case-explorer/${effectiveClientId}/opportunities/${encodeURIComponent(String(useCaseId))}/roi-model/export`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
        let message = `Failed to export ROI model (${res.status})`;
        try {
            const body = await res.json();
            if (body?.message) message = body.message;
        } catch {
            // ignore non-json error body
        }
        throw new Error(message);
    }

    const blob = await res.blob();
    const contentDisposition = res.headers.get('content-disposition') || '';
    const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch?.[1] || `roi-model-${useCaseId}.csv`;

    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
}
